import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Pagination } from '~/components/Pagination';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import { useDefaultSort } from '~/modules/settings/hooks/useDefaultSort';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import type { LabelListItem } from '~settings/api/lib/schemas';
import { TableCellSkeleton } from '~settings/components/TableCellSkeleton';
import { Toolbar } from '~settings/components/Toolbar';
import { SETTINGS_ROUTES } from '~settings/config/routes';

import { useSublabelsColumns } from './hooks/useSublabelsColumns';

const labelEmptyArray: LabelListItem[] = [];

interface SublabelProps {
  labelId?: string;
  parentLabelId?: string;
}

export function Sublabels({ parentLabelId }: SublabelProps) {
  const columns = useSublabelsColumns();
  const [isPaginationFeatureEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsPssf,
  ]);

  const { t } = useTranslation('settings');
  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const { sorting, setSorting, orderBy } = useDefaultSort({
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { searchTerm } = useSearch();

  const navigate = useNavigate();
  const onClick = (row: LabelListItem) => {
    navigate(SETTINGS_ROUTES.LABEL_DETAIL(row.id));
  };

  const { labelList, isFetching } = useLabelList({
    skip,
    take,
    labelType: 'sub_label',
    parentIds: parentLabelId,
    ...(orderBy && { orderBy }),
    ...(searchTerm && { search: searchTerm }),
  });

  return (
    <>
      {isPaginationFeatureEnabled ? (
        <>
          <Toolbar
            placeholder={t('searchLabels')}
            setPageNumber={setPageNumber}
          />
          <div className="flex justify-start">
            <Pagination
              page={page}
              total={labelList?.total ?? 0}
              perPage={perPage}
              entity="sublabels"
              setPageNumber={setPageNumber}
            />
          </div>
        </>
      ) : null}

      <div className="pt-lg">
        <Table
          CellSkeleton={TableCellSkeleton}
          columns={columns}
          data={labelList?.data ?? labelEmptyArray}
          isLoading={isFetching}
          loadingRowCount={10}
          onRowClick={onClick}
          onSortingChange={setSorting}
          size="md"
          sort={sorting}
          variant="striped"
          level={3}
        />
      </div>
    </>
  );
}

Sublabels.displayName = 'Sublabels';
